import type { ReactNode } from 'react';
import { Link } from 'react-router';
import { LEGAL_CONTENT } from './legalContent.js';
import { Icon } from '@/ui/icons.js';

/** Outside AppLayout, so this page clears the notch and home indicator and scrolls itself. */
const SAFE_PAGE = {
  paddingTop: 'calc(1.5rem + env(safe-area-inset-top))',
  paddingBottom: 'calc(1.5rem + env(safe-area-inset-bottom))',
};

export const LEGAL_DOCS = LEGAL_CONTENT.map((doc) => ({ slug: doc.slug, title: doc.title }));

/**
 * Policy pages. Reachable without signing in (store review and the landing page
 * footer both need that). In force as written; an attorney review is planned
 * before broad launch (docs/RELEASE.md).
 */
export function LegalPage({ slug }: { slug: string }): ReactNode {
  const doc = LEGAL_CONTENT.find((d) => d.slug === slug);

  if (!doc) {
    return (
      <main className="mx-auto h-dvh max-w-screen-sm overflow-y-auto p-6" style={SAFE_PAGE}>
        <p className="text-sm">
          Unknown document.{' '}
          <Link className="underline" to="/legal/privacy-policy">
            Privacy Policy
          </Link>
        </p>
      </main>
    );
  }

  return (
    <main className="mx-auto h-dvh max-w-screen-sm space-y-4 overflow-y-auto p-6" style={SAFE_PAGE}>
      <Link to="/settings" className="inline-flex items-center gap-1 text-sm font-medium text-ink">
        <Icon name="back" size={16} />
        Back
      </Link>
      <header>
        <h1 className="text-2xl font-bold tracking-tight">{doc.title}</h1>
        <p className="mt-1 text-xs text-muted">Last updated {doc.updated}</p>
        <p className="mt-3 text-sm text-ink-soft">{doc.intro}</p>
      </header>
      {doc.sections.map((section) => (
        <section key={section.heading}>
          <h2 className="mt-4 text-base font-semibold">{section.heading}</h2>
          <ul className="mt-1 space-y-1.5">
            {section.body.map((line, i) => (
              <li key={i} className="text-sm leading-relaxed text-ink-soft">
                {line}
              </li>
            ))}
          </ul>
        </section>
      ))}
      <nav className="border-t border-line pt-4 text-xs">
        <p className="mb-1 font-semibold">All documents</p>
        <ul className="grid gap-1">
          {LEGAL_DOCS.filter((d) => d.slug !== slug).map((d) => (
            <li key={d.slug}>
              <Link className="underline" to={`/legal/${d.slug}`}>
                {d.title}
              </Link>
            </li>
          ))}
        </ul>
      </nav>
    </main>
  );
}
